import { captureError } from "./sentry";
import { handleDynamicImportLoadError } from "./chunkLoadRecovery";
import { logger } from "../utils/logger";

const TOAST_THROTTLE_MS = 10000;
const MAX_TOAST_DETAIL_LENGTH = 240;
const lastToastByKey = new Map();

function errorFromUnknown(value, fallbackMessage) {
  if (value instanceof Error) return value;
  const message =
    typeof value === "string" ? value : value?.message ? String(value.message) : fallbackMessage;
  const error = new Error(message);
  if (value && typeof value === "object" && "stack" in value) {
    error.stack = String(value.stack);
  }
  return error;
}

export function getRuntimeErrorDetail(value, fallbackMessage = "Unknown runtime error") {
  const error = errorFromUnknown(value, fallbackMessage);
  const firstLine = String(error.message || fallbackMessage)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find(Boolean);
  if (!firstLine) return fallbackMessage;
  if (firstLine.length <= MAX_TOAST_DETAIL_LENGTH) return firstLine;
  return `${firstLine.slice(0, MAX_TOAST_DETAIL_LENGTH - 1)}…`;
}

export function formatRuntimeErrorToast(title, errorLike) {
  const detail = getRuntimeErrorDetail(errorLike, "");
  return detail ? `${title}: ${detail}` : title;
}

function showRuntimeToast(message, key, detail = {}) {
  if (typeof window === "undefined") return;

  const now = Date.now();
  const toastKey = key || message;
  const lastToastAt = lastToastByKey.get(toastKey) || 0;
  if (now - lastToastAt < TOAST_THROTTLE_MS) return;

  lastToastByKey.set(toastKey, now);
  window.dispatchEvent(
    new CustomEvent("critter-toast", {
      detail: { message, ...detail },
    })
  );
}

export function reportRuntimeError(
  errorLike,
  context = {},
  options = {
    toastMessage: "An unexpected runtime error occurred. Try the action again.",
  }
) {
  const error = errorFromUnknown(errorLike, "Unknown runtime error");
  const source = context?.source || "runtime";

  logger.error(`[RuntimeError:${source}]`, error, context);
  captureError(error, context);

  if (!options?.skipChunkLoadRecovery && handleDynamicImportLoadError(error)) {
    return error;
  }

  if (options?.toastMessage) {
    showRuntimeToast(options.toastMessage, options.toastKey || source, options.toastDetail);
  }

  return error;
}

export function initRuntimeErrorReporting() {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    reportRuntimeError(event.error || event.message, {
      source: "window-error",
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportRuntimeError(event.reason, {
      source: "unhandled-rejection",
    });
  });

  window.addEventListener("vite:preloadError", (event) => {
    const error = errorFromUnknown(event.payload || event.reason, "Failed to load app files");
    const handled = handleDynamicImportLoadError(error);
    if (handled) {
      event.preventDefault?.();
    }

    reportRuntimeError(
      error,
      {
        source: "vite-preload-error",
      },
      { toastMessage: null, skipChunkLoadRecovery: true }
    );
  });

  window.addEventListener("messageerror", (event) => {
    reportRuntimeError(new Error("Window message could not be deserialized"), {
      source: "window-messageerror",
      origin: event.origin,
    });
  });
}
